(function () {
    'use strict';

    angular
        .module('app')
        .directive('drvItemDeletedList', drvItemDeletedList);

    /**
     * @ngdoc directive
     * @name drvItemDeletedList
     *
     * @description
     * Show a list of deleted items.
     *
     * @param {number} parentItemId - The id of the 'parent' or 'owner' item to filter the list against.
     * @param {object} api - Optional. If provided, will have functions added to it for interacting with the item list:
     *   - {function} refresh - Refresh the item list.
     * @param {function} listUpdated - Optional. Callback, triggered when list is updated.
     * @param {string} itemType - Optional. Filter by the item type. If this is provided the Type and Deletion columns will not be shown and the deletion type will always be 'Logical'.
     */
    function drvItemDeletedList() {
        return {
            templateUrl: 'app/item/item-deleted-list.tmpl.html',
            scope: {},
            controller: drvItemDeletedListCtrl,
            controllerAs: 'drvCtrl',
            bindToController: {
                parentItemId: '<',
                api: '=',
                listUpdated: '&?',
                itemType: '<?',
                hideMenu: '<?',
                itemLabel: '<?',
                itemPluralLabel: '<?'
            }
        };
    }

    drvItemDeletedListCtrl.$inject = [
        '$q',
        '$scope',
        '$timeout',
        '$location',
        'ItemService',
        'DialogService',
        'SystemService',
        'EntitiesService',
        'Task',
        'PromiseLatest'];

    function drvItemDeletedListCtrl(
        $q,
        $scope,
        $timeout,
        $location,
        ItemService,
        DialogService,
        SystemService,
        EntitiesService,
        Task,
        PromiseLatest) {

        /* jshint validthis:true */
        var drvCtrl = this;

        drvCtrl.items = [];
        drvCtrl.itemsForAllSelectControl = [];
        drvCtrl.itemCount = 0;
        drvCtrl.currentPage = 1;
        drvCtrl.pageSize = 10;
        drvCtrl.loading = false;
        drvCtrl.error = '';
        drvCtrl.users = [];
        drvCtrl.updateList = updateList;
        drvCtrl.canRestore = canRestore;
        drvCtrl.restore = restore;
        drvCtrl.restoreSelectedItems = restoreSelectedItems;
        drvCtrl.permanentDeleteSelectedItems = permanentDeleteSelectedItems;
        drvCtrl.deletionTypeChanged = deletionTypeChanged;
        drvCtrl.getItemTypeLabel = getItemTypeLabel;
        drvCtrl.showCondensedView = showCondensedView;
        drvCtrl.showTypeColumn = showTypeColumn;
        drvCtrl.getRecordTypes = getRecordTypes;
        drvCtrl.getLabel = getLabel;
        drvCtrl.getTypeLabel = getTypeLabel;
        drvCtrl.searchDescription = '';
        drvCtrl.searchRecordType = '';
        drvCtrl.searchDeletionType = ['Logical'];
        drvCtrl.searchUser = null;
        drvCtrl.userEntityTemplateName = 'User';
        drvCtrl.recordTypes = [
            { value: 'DashboardSetting', description: 'Dashboard Panel', iconUsage: 'home' },
            { value: 'DocumentTemplate', description: 'Document Template', iconUsage: 'document template' },
            { value: 'Email', description: 'Email', iconUsage: 'email' },
            { value: 'EmailAddressCache', description: 'Email Address Cache', iconUsage: 'email address cache' },
            { value: 'EmailTemplate', description: 'Email Template', iconUsage: 'email template' },
            { value: 'Entity', description: 'Entity', iconUsage: 'entity' },
            { value: 'EntityTemplate', description: 'Entity Template', iconUsage: 'entity template' },
            { value: 'File', description: 'File', iconUsage: 'file' },
            { value: 'Note', description: 'Note', iconUsage: 'note' },
            { value: 'SMS', description: 'SMS', iconUsage: 'sms' },
            { value: 'SMSTemplate', description: 'SMS Template', iconUsage: 'sms template' },
            { value: 'Task', description: 'Task', iconUsage: 'task' },
            { value: 'WorkflowDefinition', description: 'Workflow Definition', iconUsage: 'workflow' },
            { value: 'WorkflowInstance', description: 'Workflow Instance', iconUsage: 'history' }
        ];
        drvCtrl.deletionTypes = [
            { value: 'Logical', description: 'Logical' },
            { value: 'LogicalCascade', description: 'Logical (Cascade)' },
            { value: 'Permanent', description: 'Permanent' },
            { value: 'PermanentCascade', description: 'Permanent (Cascade)' }
        ];
        drvCtrl.api = {
            restoreSelectedItems: drvCtrl.restoreSelectedItems,
            permanentDeleteSelectedItems: drvCtrl.permanentDeleteSelectedItems
        };

        var updateListPromise = new PromiseLatest();

        activate();

        function activate() {
            if (!_.isNil(drvCtrl.api)) {
                drvCtrl.api.refresh = updateList;
            }

            drvCtrl.searchDelay = SystemService.getSearchDelay();
            updateList();
            loadUsers();

            function searchVariableChanged(newVal, oldVal) {
                if (newVal != oldVal) {
                    updateList();
                }
            }

            $scope.$watch('drvCtrl.parentItemId', searchVariableChanged);
            $scope.$watch('drvCtrl.itemType', searchVariableChanged);
            $scope.$watch('drvCtrl.searchRecordType', searchVariableChanged);
            $scope.$watch('drvCtrl.searchDeletionType', searchVariableChanged);
            $scope.$watch('drvCtrl.searchDescription', searchVariableChanged);
            $scope.$watch('drvCtrl.searchUser', searchVariableChanged);
        }

        /**
         * Update the data for the item list.
         *
         * @param {number} delay - Optional. A delay, in milliseconds, for debounce-like functionality.
         *   data will not be updated until the delay elapses, unless a subsequent call with a shorter
         *   remaining delay is made.
         */
        function updateList(delay) {
            if (!_.isNil(updateList.timeout)) {
                $timeout.cancel(updateList.timeout);
                updateList.timeout = null;
            }

            if (_.isNil(delay) || delay === 0) {
                getItems();
            } else {
                $timeout(getItems, delay);
            }

            function getItems() {
                var recordType = drvCtrl.searchRecordType;
                if ((_.isNil(recordType) || recordType.length === 0) && !_.isNil(drvCtrl.itemType) && drvCtrl.itemType !== '') {
                    recordType = drvCtrl.itemType;
                }

                // If it's a condensed view, we need to show logical and logical cascaded items
                var searchDeletionType = ['Logical', 'LogicalCascade'];
                if (!showCondensedView()) {
                    searchDeletionType = drvCtrl.searchDeletionType;
                }

                drvCtrl.loading = true;

                updateListPromise.latestOnly(ItemService.getItemsByParent(drvCtrl.parentItemId, {
                    recordType: recordType,
                    deletionTypes: searchDeletionType,
                    description: drvCtrl.searchDescription,
                    userId: drvCtrl.searchUser,
                    tagIds: drvCtrl.searchTagIds,
                    pageNumber: drvCtrl.currentPage,
                    pageSize: drvCtrl.pageSize,
                    excludeLinkTypes: ['Entity Image']
                }))
                    .then(getItemsOk, getItemsError)
                    .finally(function () { drvCtrl.loading = false; });
            }

            function getItemsOk(items) {
                _.forEach(items.Items, function (item) {
                    formatItemForView(item);
                });

                _.forEach(items.Items, function (item) {
                    _.forEach(item.Owners, function (itemOwner) {
                        formatItemForView(itemOwner, true);
                    });
                });

                drvCtrl.items = items.Items;
                drvCtrl.itemCount = items.ItemCount;
                drvCtrl.itemsForAllSelectControl = _.filter(items.Items, function (item) {
                    return item.DeletionType === 'Logical';
                });

                if (angular.isFunction(drvCtrl.listUpdated)) {
                    drvCtrl.listUpdated();
                }
            }

            function getItemsError(response) {
                drvCtrl.error = response;
            }

            /**
             * Format an "Item" for viewing. Some properties may be added, others may be modified.
             *
             * @param {object} item - The item to be formatted.
             * @param {bool} includeTypePrefix - True if item.Description should be prefixed with
             *   the item type / entity template name.
             */
            function formatItemForView(item, includeTypePrefix) {
                item.iconUsage = item.Type;
                item.icon = '';

                if (item.Type === 'SMS') {
                    item.iconUsage = '';
                    item.icon = 'sms-sent';
                } else if (item.Type === 'Email') {
                    if (item.EmailStatus === 'Sent') {
                        item.iconUsage = 'email-sent';
                    } else if (item.EmailStatus === 'Received') {
                        item.iconUsage = 'email-received';
                    } else if (item.EmailStatus === 'Draft') {
                        item.iconUsage = 'email-draft';
                    }
                } else if (item.Type === 'Entity') {
                    item.iconUsage = '';
                    item.icon = item.EntityTemplateIcon;
                    item.iconColour = item.EntityTemplateIconColour;
                } else if (item.Type === 'EntityTemplate') {
                    item.iconUsage = 'entity template';
                } else if (item.Type === 'EmailAddressCache') {
                    item.iconUsage = 'email address cache';
                } else if (item.Type === 'DocumentTemplate') {
                    item.iconUsage = 'document template';
                } else if (item.Type === 'EmailTemplate') {
                    item.iconUsage = 'email template';
                } else if (item.Type === 'SMSTemplate') {
                    item.iconUsage = 'sms template';
                } else if (item.Type === 'DashboardSetting') {
                    item.iconUsage = 'home';
                } else if (item.Type === 'WorkflowDefinition') {
                    item.iconUsage = 'workflow';
                } else if (item.Type === 'WorkflowInstance') {
                    item.iconUsage = 'history';
                }

                if (includeTypePrefix) {
                    if (item.EntityTemplateName) {
                        item.Description = item.EntityTemplateName + ' - ' + item.Description;
                    } else {
                        item.Description = item.Type + ' - ' + item.Description;
                    }
                }
            }

        }

        /**
         * Load all users.
         */
        function loadUsers() {
            return EntitiesService.query({ entityType: 'User' })
                .then(getUsersOk, getUsersError);

            function getUsersOk(data) {
                drvCtrl.users = data.Entities;
                
                if (data.Entities.length > 0) {
                    drvCtrl.userEntityTemplateName = data.Entities[0].EntityTemplate.Name;
                }
            }

            function getUsersError(response) {
                DialogService.error('Error fetching Users', response);
            }
        }

        function canRestore(item) {
            return !_.isNil(item) && (item.DeletionType === 'Logical');
        }

        function restore(item) {
            DialogService.confirm('Restore the ' + drvCtrl.getTypeLabel(item) + ' \'' + item.Description + '\'?', drvCtrl.getLabel() + ' restore confirm')
                .then(restoreConfirmed);

            function restoreConfirmed() {
                drvCtrl.loading = true;

                ItemService.restoreItem(item.Id)
                    .then(restoreItemOk, restoreItemError)
                    .finally(function () { drvCtrl.loading = false; });
            }
        }

        function restoreSelectedItems() {
            var itemIds = getSelectedItemIds();

            if (ensureItemSelection()) {
                DialogService.confirm('Restore the ' + itemIds.length + ' ' + drvCtrl.getLabel(itemIds.length > 1) + '?', drvCtrl.getLabel() + ' restore confirm')
                    .then(restoreConfirmed);
            }

            function restoreConfirmed() {
                drvCtrl.loading = true;

                ItemService.restoreItems(itemIds)
                    .then(restoreItemOk, restoreItemError)
                    .finally(function () { drvCtrl.loading = false; });
            }
        }

        function restoreItemOk() {
            drvCtrl.updateList();
        }

        function restoreItemError(response) {
            drvCtrl.error = response;
        }

        function permanentDeleteSelectedItems() {

        }

        function deletionTypeChanged() {
            if (_.isNil(drvCtrl.searchDeletionType) || drvCtrl.searchDeletionType.length === 0) {
                drvCtrl.searchDeletionType = ['Logical'];
            }
        }

        function getItemTypeLabel(item) {
            var result = item.Type;

            switch (item.Type) {
                case 'DocumentTemplate':
                    result = 'Document Template';
                    break;
                case 'EmailAddressCache':
                    result = 'Email Address Cache';
                    break;
                case 'EmailTemplate':
                    result = 'Email Template';
                    break;
                case 'Entity':
                    result = item.EntityTemplateName;
                    break;
                case 'EntityTemplate':
                    result = 'Entity Template';
                    break;
                case 'SMSTemplate':
                    result = 'SMS Template';
                    break;
                case 'DashboardSetting':
                    result = 'Dashboard Panel';
                    break;
                case 'WorkflowDefinition':
                    result = 'Workflow Definition';
                    break;
                case 'WorkflowInstance':
                    result = 'Workflow Instance';
                    break;
                default:
                    break;
            }

            return result;
        }

        /**
         * Get the Id's of the items selected in the view.
         *
         * @return {array} - Array of {number}
         */
        function getSelectedItemIds() {
            return _.chain(drvCtrl.items).filter('Selected').map('Id').value();
        }

        /**
         * Ensure at least one Item is selected.
         * Displays a dialog 'No Items were selected' if no Items are selected.
         *
         * @return {bool} true if at least one Item is selected, else false
         */
        function ensureItemSelection() {
            if (getSelectedItemIds().length === 0) {
                DialogService.alert('No ' + drvCtrl.getLabel(true) + ' were selected.');
                return false;
            } else {
                return true;
            }
        }

        function showCondensedView() {
            var result = false;

            if (!_.isNil(drvCtrl.itemType) && drvCtrl.itemType !== '') {
                result = true;
            }

           return result;
        }

        function showTypeColumn() {
            var result = false;

            if (!_.isNil(drvCtrl.itemType) && Array.isArray(drvCtrl.itemType) && drvCtrl.itemType.length > 1) {
                result = true;
            }

            return result;
        }

        function getRecordTypes() {
            if (showTypeColumn()) {
                return _.filter(drvCtrl.recordTypes, function (recordType) { return _.some(drvCtrl.itemType, function (itemType) { return itemType === recordType.value; }); });
            } else {
                return drvCtrl.recordTypes;
            }
        }

        /**
         * Get the items label
         *
         * @returns {string}
         */
        function getLabel(plural) {
            var result = '';

            if (plural === true && !_.isNil(drvCtrl.itemPluralLabel) && drvCtrl.itemPluralLabel !== '') {
                result = drvCtrl.itemPluralLabel;
            } else if (!_.isNil(drvCtrl.itemLabel) && drvCtrl.itemLabel !== '') {
                result = drvCtrl.itemLabel;
            } else {
                if (plural === true) {
                    result = 'Items';
                } else {
                    result = 'Item';
                }
            }

            return result;
        }

        /**
         * Get the items type label
         *
         * @returns {string}
         */
        function getTypeLabel(item) {
            var result = item.Type;

            if (!_.isNil(!_.isNil(drvCtrl.itemLabel) && drvCtrl.itemLabel !== '')) {
                result = drvCtrl.itemLabel;
            }

            return result;
        }
    }
})();
